/**
 * The day view and the Summary page's Pilots box (TJR inventory 02 §7 and §15.2; gap list #21, #48, #53, #68).
 *
 * - Day view: every entry of a date in flying order with TJR's computed Daily Flight columns: landing time
 *   (take-off + hours flown), Prog TTIS (the aircraft's TTIS after the entry), km flown and accepted from line status
 *   (decision 32 basis and the `reportTotals` rule), and the day's totals.
 * - Pilots box: per pilot, survey, comp box, cal and ferry hours (comp box and cals apart, MB decision 162; TJR's
 *   combined "Cal Hours" as `tjr_cal_h`), total hours, km flown and accepted, refly km and
 *   accepted km per survey hour. Both pilot slots are credited (MB, decision 26): a flight with a captain and a
 *   training captain counts for each of them. TJR's figures for Kyle Gardner on 14 Oct 2026: 14.50 h survey,
 *   2 991.59 km flown and accepted, 206.32 km/h.
 * - Refly km (TJR's formula is not in the decompiled sources): the km still to fly from the pilot's flights, the
 *   flown km of a rejected line and flown − accepted of a part-accepted line.
 */
import { chronological, hoursFlown, landingTime, progressiveTtis } from './aircraft.js';
import type { Aircraft, DailyEntry, Flight, LineStatus, Uuid } from './model.js';
import { flownCreditM } from './progress.js';
import { acceptedM, entryLineStatuses } from './tjr-adapter.js';
import { normalisePersonName, personNameKey } from './staff.js';

export interface DayRow {
  readonly entry: DailyEntry;
  readonly landing: string | null;
  readonly hours_flown_h: number;
  /** The aircraft's TTIS after this entry; null without TTIS at start. */
  readonly prog_ttis_h: number | null;
  readonly flown_km: number;
  readonly accepted_km: number;
  readonly lines: number;
}

export interface DayTotals {
  readonly entries: number;
  readonly flights: number;
  readonly survey_h: number;
  readonly comp_box_h: number;
  readonly cal_h: number;
  readonly ferry_h: number;
  readonly external_h: number;
  readonly hours_flown_h: number;
  readonly fuel_used_l: number;
  readonly refuel_l: number;
  readonly flown_km: number;
  readonly accepted_km: number;
  readonly lines: number;
}

export interface DaySummary {
  readonly date: string;
  readonly rows: readonly DayRow[];
  readonly totals: DayTotals;
}

/** Refly km of statuses: rejected lines' flown km, part-accepted lines' flown − accepted. */
export function reflyM(
  statuses: readonly Pick<
    LineStatus,
    'acceptance' | 'accepted_m' | 'log_completion' | 'flown_m' | 'proposed_m'
  >[],
): number {
  return statuses.reduce((a, s) => {
    if (s.acceptance === 'rejected') return a + flownCreditM(s);
    if (s.acceptance === 'partial') return a + Math.max(0, flownCreditM(s) - s.accepted_m);
    return a;
  }, 0);
}

/** The day view for `date` (`YYYY-MM-DD`). */
export function daySummary(
  date: string,
  entries: readonly DailyEntry[],
  aircraft: readonly Aircraft[],
  statuses: readonly LineStatus[],
  flights: readonly Flight[],
): DaySummary {
  const live = entries.filter((e) => e.deleted_at === null);
  const rows: DayRow[] = dayRowsOf(
    live,
    live.filter((e) => e.date === date),
    aircraft,
    statuses,
    flights,
  );
  return { date, rows, totals: dayTotals(rows) };
}

/** The day's totals: the sum of its rows. */
export function dayTotals(rows: readonly DayRow[]): DayTotals {
  const sum = (f: (r: DayRow) => number): number => rows.reduce((s, r) => s + f(r), 0);
  return {
    entries: rows.length,
    flights: rows.filter((r) => r.entry.entry_type === 'flight').length,
    survey_h: sum((r) => r.entry.svy_hours),
    comp_box_h: sum((r) => r.entry.cbx_hours),
    cal_h: sum((r) => r.entry.cal_hours),
    ferry_h: sum((r) => r.entry.fry_hours),
    external_h: sum((r) => r.entry.ext_hours),
    hours_flown_h: sum((r) => r.hours_flown_h),
    fuel_used_l: sum((r) => r.entry.fuel_used_l),
    refuel_l: sum((r) => r.entry.fuel_load_l),
    flown_km: sum((r) => r.flown_km),
    accepted_km: sum((r) => r.accepted_km),
    lines: sum((r) => r.lines),
  };
}

/**
 * The Day view row of `shown` entries in flying order, the project's `live` entries giving each its Prog TTIS and
 * its lines. Worked out once, however many dates the entries span.
 */
function dayRowsOf(
  live: readonly DailyEntry[],
  shown: readonly DailyEntry[],
  aircraft: readonly Aircraft[],
  statuses: readonly LineStatus[],
  flights: readonly Flight[],
): DayRow[] {
  const ofEntry = entryLineStatuses(live, statuses, flights);
  const ttis = new Map<Uuid, number>();
  for (const a of aircraft) for (const [id, t] of progressiveTtis(a, live)) ttis.set(id, t);
  return chronological(shown).map((e) => {
    const own = ofEntry.get(e.id) ?? [];
    return {
      entry: e,
      landing: e.entry_type === 'flight' ? landingTime(e) : null,
      hours_flown_h: hoursFlown(e),
      prog_ttis_h: ttis.get(e.id) ?? null,
      flown_km: own.reduce((s, x) => s + flownCreditM(x), 0) / 1000,
      accepted_km: acceptedM(own) / 1000,
      lines: own.length,
    };
  });
}

/**
 * Every live entry with the Day view's columns, in flying order (MB's R60: the Daily entry table). The same rows
 * `daySummary` gives day by day, computed in one pass so a project with hundreds of entries stays fast.
 */
export function entryRows(
  entries: readonly DailyEntry[],
  aircraft: readonly Aircraft[],
  statuses: readonly LineStatus[],
  flights: readonly Flight[],
): DayRow[] {
  const live = entries.filter((e) => e.deleted_at === null);
  return dayRowsOf(live, live, aircraft, statuses, flights);
}

/** Dates that have entries, newest first. */
export function entryDates(entries: readonly DailyEntry[]): string[] {
  return [...new Set(entries.filter((e) => e.deleted_at === null).map((e) => e.date))].sort().reverse();
}

export interface PilotSummary {
  /** The register person, or `removed:<name key>` for a pilot removed for good from the register (decision 602). */
  readonly person_id: Uuid;
  /** The name the entries kept for a pilot removed for good from the register; null for a register person. */
  readonly kept_name: string | null;
  readonly flights: number;
  readonly survey_h: number;
  readonly comp_box_h: number;
  readonly cal_h: number;
  readonly ferry_h: number;
  /** TJR's "Cal Hours": comp box + cals, kept for the TJR figures (MB, decision 162, Q141). */
  readonly tjr_cal_h: number;
  /** Survey + comp box + cals + ferry. */
  readonly total_h: number;
  readonly flown_km: number;
  readonly accepted_km: number;
  readonly refly_km: number;
  /** Accepted km ÷ survey hours (TJR "Avg KM/Hr"); 0 without survey hours. */
  readonly km_per_h: number;
}

/** TJR's Pilots box for every pilot who flew, crediting pilot 1 and pilot 2. Entries up to `toDate` when given. */
export function pilotSummaries(
  entries: readonly DailyEntry[],
  statuses: readonly LineStatus[],
  flights: readonly Flight[],
  toDate?: string,
): PilotSummary[] {
  const live = entries.filter(
    (e) => e.deleted_at === null && e.entry_type === 'flight' && (toDate === undefined || e.date <= toDate),
  );
  const ofEntry = entryLineStatuses(live, statuses, flights);
  type Acc = { -readonly [K in keyof PilotSummary]: PilotSummary[K] };
  const acc = new Map<Uuid, Acc>();
  for (const e of live) {
    const own = ofEntry.get(e.id) ?? [];
    // a pilot removed for good from the register (decision 602) counts under the name the entry kept
    const slots: [Uuid | null, string | null][] = [
      [e.pilot_id, e.pilot_name ?? null],
      [e.pilot2_id ?? null, e.pilot2_name ?? null],
    ];
    const keyed = new Map<string, string | null>();
    for (const [id, kept] of slots) {
      if (id !== null) keyed.set(id, null);
      else if (kept && kept.trim() !== '')
        keyed.set(`removed:${personNameKey(kept)}`, normalisePersonName(kept));
    }
    for (const [id, keptName] of keyed) {
      const p =
        acc.get(id) ??
        ({
          person_id: id,
          kept_name: keptName,
          flights: 0,
          survey_h: 0,
          comp_box_h: 0,
          cal_h: 0,
          ferry_h: 0,
          tjr_cal_h: 0,
          total_h: 0,
          flown_km: 0,
          accepted_km: 0,
          refly_km: 0,
          km_per_h: 0,
        } satisfies Acc);
      p.flights += 1;
      p.survey_h += e.svy_hours;
      p.comp_box_h += e.cbx_hours;
      p.cal_h += e.cal_hours;
      p.ferry_h += e.fry_hours;
      p.tjr_cal_h += e.cbx_hours + e.cal_hours;
      p.total_h += hoursFlown(e);
      p.flown_km += own.reduce((s, x) => s + flownCreditM(x), 0) / 1000;
      p.accepted_km += acceptedM(own) / 1000;
      p.refly_km += reflyM(own) / 1000;
      acc.set(id, p);
    }
  }
  return [...acc.values()].map((p) => ({ ...p, km_per_h: p.survey_h > 0 ? p.accepted_km / p.survey_h : 0 }));
}

// ---------------------------------------------------------------------------------------------- crew filter (N33)

/** The register people an entry names in its crew slots: pilot, second pilot, operators (TJR's "Highlight Pilot"). */
export function entryCrewIds(
  e: Pick<DailyEntry, 'pilot_id' | 'pilot2_id' | 'operator1_id' | 'operator2_id'>,
): string[] {
  return [e.pilot_id, e.pilot2_id, e.operator1_id, e.operator2_id].filter((id): id is string => !!id);
}

/** Whether the entry names this person in any crew slot. */
export function entryHasCrew(
  e: Pick<DailyEntry, 'pilot_id' | 'pilot2_id' | 'operator1_id' | 'operator2_id'>,
  personId: string,
): boolean {
  return entryCrewIds(e).includes(personId);
}

/**
 * The crew members to filter the daily entries by (N33, TJR inventory 02 #54: TJR coloured one pilot's rows,
 * Teslyn filters to them): every person a live entry names, pilots and operators alike, by name. A person no longer
 * in the register is left out (their entries keep the name as text, decision 602).
 */
export function entryCrewChoices<P extends { readonly id: string; readonly name: string }>(
  entries: readonly DailyEntry[],
  people: readonly P[],
): P[] {
  const named = new Set(entries.filter((e) => !e.deleted_at).flatMap(entryCrewIds));
  return people.filter((p) => named.has(p.id)).sort((a, b) => a.name.localeCompare(b.name, 'en-AU'));
}
